import * as React from 'react'
import {cva, type VariantProps} from 'class-variance-authority'
import {cn} from '@/lib/utils'

const eyebrowVariants = cva('font-eyebrow font-medium', {
    variants: {
        size: {
            small: 'eyebrow-sm',
            large: 'eyebrow-lg'
        },
        type: {
            pill: 'rounded-full px-3 py-1 bg-primary',
            icon: 'inline-flex items-center gap-2 px-1 py-1 bg-primary',
            text: 'bg-transparent',
            rounded: 'rounded-[5px] px-1 py-1 bg-primary',
            square: 'rounded-none px-2 py-1 bg-primary'
        }
    }
})

type Size = 'small' | 'large'
type HtmlTag = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'div' | 'span' | 'p'

interface EyebrowProps
    extends React.HTMLAttributes<HTMLElement>,
        VariantProps<typeof eyebrowVariants> {
    size?: Size
    textColor?: string
    eyebrowBackgroundColor?: string
    type?: 'pill' | 'icon' | 'text' | 'rounded' | 'square'
    tag?: HtmlTag
    'data-testid'?: string
}

const isValidHtmlTag = (tag: string): tag is HtmlTag => {
    return ['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'div', 'span', 'p'].includes(tag)
}

const Eyebrow = React.forwardRef<HTMLElement, EyebrowProps>(
    (
        {
            size,
            type,
            tag = 'span',
            className,
            children,
            textColor = '#000000',
            eyebrowBackgroundColor = '#E0E0E0',
            'data-testid': dataTestId,
            ...props
        },
        ref
    ) => {
        const Tag = isValidHtmlTag(tag) ? tag : 'span'

        const style: React.CSSProperties = {
            backgroundColor: type === 'text' ? 'transparent' : eyebrowBackgroundColor,
            color: textColor,
            textTransform: type === 'text' ? 'capitalize' : 'uppercase'
        }

        return (
            <Tag
                className={cn(
                    eyebrowVariants({
                        size,
                        type
                    }),
                    className
                )}
                ref={ref as React.Ref<any>}
                {...props}
                data-testid={dataTestId || 'eyebrow-Tag'}
                style={style}
            >
                {children}
            </Tag>
        )
    }
)

Eyebrow.displayName = 'Eyebrow'

export {Eyebrow}
